import React from 'react';
import PropTypes from 'prop-types';
import { VOTES } from '../../../helpers/constants';
import styled from 'styled-components';
import SecondaryButton from '../../../shared/components/buttons/SecondaryButton';

const VoterRow = styled.div`
  & + & {
    margin-top: 12px;
  }
`;
const InvalidNotice = styled.div`
  color: #e74c3c;
  font-weight: 700;
`;
const Status = styled.div`
  color: ${({ color, invalid }) => (invalid ? '#9e9e9e' : color)};
  ${({ invalid }) => invalid && 'text-decoration: line-through;'}
`;
const StatusLabel = styled.span`
  text-transform: uppercase;
`;
const AttachmentLink = styled.a`
  color: inherit;
  text-decoration: underline;
`;
const DeleteVote = styled.button`
  border: none;
  background: none;
  color: #e74c3c;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 4px;
  margin-left: 6px;
`;

const renderVotedBy = votedBy => {
  if (!votedBy) {
    return null;
  }

  if (typeof votedBy === 'string') {
    return ` (${votedBy})`;
  }

  const { name, mode, verb, attachment } = votedBy;

  if (mode === 'entered' && attachment) {
    return (
      <>
        {' ('}
        <AttachmentLink
          href={attachment.path}
          target="_blank"
          rel="noopener noreferrer"
          download
        >
          {`${verb} ${name}`}
        </AttachmentLink>
        {')'}
      </>
    );
  }

  if (mode === 'certifies') {
    return ` (poświadcza ${name})`;
  }

  return ` (${name} osobiście)`;
};

/**
 * Snapshots taken before votes were stored per user keep one vote on the share.
 */
const ShareVoteStatus = ({ vote, votedBy, voteInvalid }) => {
  const voteProps = Object.values(VOTES).find(item => vote === item.value);

  if (!vote || !voteProps) {
    return null;
  }

  return (
    <>
      {voteInvalid && <InvalidNotice>rozbieżność głosów!</InvalidNotice>}
      <Status color={voteProps.color} invalid={voteInvalid}>
        <StatusLabel>{voteProps.label}</StatusLabel>
        {renderVotedBy(votedBy)}
      </Status>
    </>
  );
};

const VoteStatus = props => {
  const { canVote, voteInvalid, users, onVote, onDelete, shareVote, shareVotedBy } = props;
  const voters = users.filter(user => user.id);
  const hasVotesPerUser = voters.some(user => user.vote !== undefined);

  if (!hasVotesPerUser) {
    return (
      <ShareVoteStatus
        vote={shareVote}
        votedBy={shareVotedBy}
        voteInvalid={voteInvalid}
      />
    );
  }

  return (
    <>
      {voteInvalid && <InvalidNotice>rozbieżność głosów!</InvalidNotice>}
      {voters.map(user => {
        const vote = user.vote || 0;
        const voteProps = Object.values(VOTES).find(item => vote === item.value);

        return (
          <VoterRow key={user.id}>
            {vote !== 0 && voteProps && (
              <Status color={voteProps.color} invalid={voteInvalid} small>
                <StatusLabel>{voteProps.label}</StatusLabel>
                {renderVotedBy(user.voted_by)}
                {canVote && user.resolution_id && (
                  <DeleteVote
                    type="button"
                    title="Usuń głos"
                    onClick={() => onDelete(user.resolution_id)}
                  >
                    ✕
                  </DeleteVote>
                )}
              </Status>
            )}
            {canVote && (
              <SecondaryButton onClick={() => onVote(user.id)} small>
                {vote === 0 ? 'Głosuj' : 'Zmień głos'}
              </SecondaryButton>
            )}
          </VoterRow>
        );
      })}
    </>
  );
};

VoteStatus.propTypes = {
  canVote: PropTypes.bool,
  voteInvalid: PropTypes.bool,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      display_name: PropTypes.string,
      vote: PropTypes.number,
      resolution_id: PropTypes.number,
      voted_by: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          mode: PropTypes.oneOf(['personally', 'certifies', 'entered']),
          verb: PropTypes.string,
          attachment: PropTypes.shape({
            name: PropTypes.string,
            mime_type: PropTypes.string,
            path: PropTypes.string
          })
        })
      ])
    })
  ),
  onVote: PropTypes.func,
  onDelete: PropTypes.func,
  shareVote: PropTypes.number,
  shareVotedBy: PropTypes.string
};

VoteStatus.defaultProps = {
  canVote: false,
  users: [],
  onVote: null,
  onDelete: null
};

export default VoteStatus;
